import { createAuthClient } from "better-auth/react";
import {
  organizationClient,
  adminClient,
  twoFactorClient,
} from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";

export const BEARER_TOKEN_KEY = "bearer_token";

/**
 * Bearer token handling — backed by better-auth's server-side `bearer()` plugin.
 *
 * The server plugin emits a `set-auth-token` response header on successful signIn
 * (and other auth calls). We capture it via `fetchOptions.onSuccess` and stash it
 * in localStorage. The `fetchOptions.auth.token` callback re-attaches it to every
 * outgoing better-auth client call. For the general API surface (everything that
 * doesn't go through this client), the global fetch wrapper in api.ts adds the
 * Authorization header from the same localStorage key.
 *
 * There is no `bearerClient()` plugin in better-auth — bearer is purely server-side;
 * the client cooperates via the fetchOptions hooks above.
 */
export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_AUTH_BASE_URL || "http://localhost:3500",
  plugins: [
    organizationClient(),
    adminClient(),
    // When a sign-in succeeds but the user has 2FA enabled, better-auth
    // returns `{ twoFactorRedirect: true }` instead of a session. The hook
    // routes the user to the /two-factor page where they enter their TOTP.
    twoFactorClient({
      onTwoFactorRedirect() {
        window.location.href = "/two-factor";
      },
    }),
    passkeyClient(),
  ],
  fetchOptions: {
    auth: {
      type: "Bearer",
      token: () => localStorage.getItem(BEARER_TOKEN_KEY) || "",
    },
    onSuccess: (ctx) => {
      const authToken = ctx.response.headers.get("set-auth-token");
      if (authToken) {
        localStorage.setItem(BEARER_TOKEN_KEY, authToken);
      }
    },
  },
});
